import { useState, useContext, createContext, useEffect, FC } from 'react';
import GlobalContext from './GlobalContext';
import { validateEmail } from '../util/validation/formValidation';
import { TCreateAccountPost, TRequestVerifyPost } from '../api/types';
import API from '../api/api';
import { validatePassword } from '../util/validation/passwordValidation';
import { useLocation, useHistory } from 'react-router';
import { TCreateFamilyAccountRouterState } from './CreateFamilyAccountContext';
import {
    EFamilyAccountType,
    ERoutes,
    maxNameLength,
    maxEmailLength,
    ELDFlags,
    EMixpanelEvent,
} from '../util/constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import mixpanel from 'mixpanel-browser';

export enum ECreateAccountUiState {
    default,
    ready,
    confirm,
    success,
}

export type TCreateAccountRouterState = {
    prePopulateForm?: { firstName?: string; lastName?: string; email?: string; password?: string; country?: string };
    errorMessage?: string;
};

export type TCreateAccountContext = {
    createAccountUiState: ECreateAccountUiState;
    setCreateAccountUiState: (createAcccountUiState: ECreateAccountUiState) => void;
    isPrePopulated: boolean;
    firstName: string;
    setFirstName: (firstName: string) => void;
    firstNameIsValid: boolean;
    firstNameValidationMessage: string;
    lastName: string;
    setLastName: (lastName: string) => void;
    lastNameIsValid: boolean;
    lastNameValidationMessage: string;
    email: string;
    emailIsValid: boolean;
    emailValidationMessage: string;
    setEmail: (email: string) => void;
    password: string;
    passwordIsValid: boolean;
    setPassword: (password: string) => void;
    country: string;
    setCountry: (country: string) => void;
    countryIsValid: boolean;
    addFamily: () => void;
    skipAddFamily: () => void;
    submitCreateAccountForm: () => Promise<void>;
    errorMessage: string;
    tosAgreed: boolean;
    setTosAgreed: (tosAgreed: boolean) => void;
    tosIsValid: boolean;
    marketingOptIn: boolean;
    setMarketingOptIn: (marketingOptIn: boolean) => void;
    submitVerifyPasscode: (passcode: string) => Promise<void>;
    resendVerifyPasscode: () => Promise<void>;
};

const CreateAccountContext = createContext<TCreateAccountContext | undefined>(undefined);

export const CreateAccountProvider: FC = ({ children }) => {
    const {
        language,
        gatewayAPIBaseUrl,
        getCountriesList,
        countriesList,
        subscriberId,
        coBrandingChecked,
        getCoBranding,
        putFullLoginLinkInLocalStorage,
        updateLaunchDarklyContext,
    } = useContext(GlobalContext);
    const history = useHistory();
    const { state: routerState = {} }: { state: TCreateAccountRouterState } = useLocation();
    const [createAccountUiState, setCreateAccountUiState] = useState(ECreateAccountUiState.default);
    const [identityUserId, setIdentityUserId] = useState('');
    const [firstName, setFirstName] = useState('');
    const [firstNameIsValid, setFirstNameIsValid] = useState(true);
    const [firstNameValidationMessage, setFirstNameValidationMessage] = useState('');
    const [lastName, setLastName] = useState('');
    const [lastNameIsValid, setLastNameIsValid] = useState(true);
    const [lastNameValidationMessage, setLastNameValidationMessage] = useState('');
    const [email, setEmail] = useState('');
    const [emailIsValid, setEmailIsValid] = useState(true);
    const [emailValidationMessage, setEmailValidationMessage] = useState('');
    const [password, setPassword] = useState('');
    const [passwordIsValid, setPasswordIsValid] = useState(true);
    const [country, setCountry] = useState('');
    const [countryIsValid, setCountryIsValid] = useState(true);
    const [tosAgreed, setTosAgreed] = useState(false);
    const [tosIsValid, setTosIsValid] = useState(true);
    const [marketingOptIn, setMarketingOptIn] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const isPrePopulated = routerState.hasOwnProperty('prePopulateForm');
    const { [ELDFlags.useConsumerPasscodeVerification]: usePasscodeForVerifiication } = useFlags();

    const getInitialData = async () => {
        !countriesList.length && (await getCountriesList());
        !coBrandingChecked && (await getCoBranding());
        if (isPrePopulated) {
            const { firstName, lastName, email, password, country } = routerState.prePopulateForm;
            firstName && setFirstName(firstName);
            lastName && setLastName(lastName);
            email && setEmail(email);
            password && setPassword(password);
            country && setCountry(country);
        }
        if (routerState.hasOwnProperty('errorMessage')) {
            const { errorMessage } = routerState;
            setErrorMessage(errorMessage);
        }
        setCreateAccountUiState(ECreateAccountUiState.ready);
    };

    const checkEmailIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!email.length) {
            isValid = false;
            validationMessage = language.validation.emailMissing;
        } else if (!validateEmail(email)) {
            isValid = false;
            validationMessage = language.validation.email;
        }
        if (email.length > maxEmailLength) {
            isValid = false;
            validationMessage = language.validation.emailMaxLength;
        }
        setEmailIsValid(isValid);
        setEmailValidationMessage(validationMessage);
        return isValid;
    };

    const checkFirstNameIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!firstName.length) {
            isValid = false;
            validationMessage = language.validation.firstName;
        } else if (firstName.length > maxNameLength) {
            isValid = false;
            validationMessage = language.validation.firstNameMaxLength;
        }
        setFirstNameIsValid(isValid);
        setFirstNameValidationMessage(validationMessage);
        return isValid;
    };

    const checkLastNameIsValid = () => {
        let isValid = true;
        let validationMessage = '';
        if (!lastName.length) {
            isValid = false;
            validationMessage = language.validation.lastName;
        }
        if (lastName.length > maxNameLength) {
            isValid = false;
            validationMessage = language.validation.lastNameMaxLength;
        } else if (lastName.length > maxNameLength) {
            setLastNameIsValid(false);
            setLastNameValidationMessage(language.validation.lastNameMaxLength);
        }
        setLastNameIsValid(isValid);
        setLastNameValidationMessage(validationMessage);
        return isValid;
    };

    const checkPasswordIsValid = () => {
        const isValid = validatePassword(password);
        setPasswordIsValid(isValid);
        return isValid;
    };

    const checkCountryIsValid = () => {
        const isValid = country ? true : false;
        setCountryIsValid(isValid);
        return isValid;
    };

    const checkTermsIsValid = () => {
        let isValid = true;
        if (!tosAgreed) {
            isValid = false;
        } else {
            isValid = true;
        }
        setTosIsValid(isValid);
        return isValid;
    };

    const validateForm = (checkTerms: boolean) => {
        const emailIsValid = checkEmailIsValid();
        const firstNameIsValid = checkFirstNameIsValid();
        const lastNameIsValid = checkLastNameIsValid();
        const passwordIsValid = checkPasswordIsValid();
        const countryIsValid = checkCountryIsValid();
        const termsIsValid = checkTerms ? checkTermsIsValid() : true;
        if (emailIsValid && firstNameIsValid && lastNameIsValid && passwordIsValid && countryIsValid && termsIsValid) {
            return true;
        } else {
            return false;
        }
    };

    const addFamily = () => {
        const formIsValid = validateForm(false);
        if (formIsValid) {
            const userData = { firstName, lastName, email, password, country };
            const createAccountRouterState: TCreateAccountRouterState = {
                prePopulateForm: userData,
            };
            const createFamilyAccountRouterState: TCreateFamilyAccountRouterState = {
                familyAccountType: EFamilyAccountType.firstParty,
                primaryUser: { firstName, lastName, email, password, country },
            };
            // replace history state to preserve form values when navigating via back button
            history.replace({
                state: createAccountRouterState,
            });
            // then go to crete family account screen
            history.push({
                pathname: ERoutes.createFamilyAccount,
                state: createFamilyAccountRouterState,
            });
        }
    };

    const skipAddFamily = () => {
        const formIsValid = validateForm(false);
        if (formIsValid) {
            setCreateAccountUiState(ECreateAccountUiState.confirm);
        }
    };

    const submitCreateAccountForm = async () => {
        const formIsValid = validateForm(true);
        if (formIsValid) {
            const postObject: TCreateAccountPost = {
                firstName,
                lastName,
                email,
                password,
                countryCode: country,
                marketingOptIn: marketingOptIn,
                ...(subscriberId && { subscriberId }),
                ...(usePasscodeForVerifiication && { usePasscode: true }),
            };
            try {
                const response = await API.post.createAccount(gatewayAPIBaseUrl, postObject);
                const { id } = response.data;
                setIdentityUserId(id);
                await updateLaunchDarklyContext(id);
                setCreateAccountUiState(ECreateAccountUiState.success);
                mixpanel.identify(id);
            } catch (error) {
                console.error('Failed to create account');
                if (error.response.status === 409) {
                    throw Error('Failed to create account due to email conflict', { cause: 409 });
                }
                throw Error('Failed to create account', { cause: error });
            }
        } else {
            throw Error('Create account form is invalid', { cause: 'invalid' });
        }
    };

    const submitVerifyPasscode = async (passcode: string) => {
        const queryParams = `?code=${passcode}&subscriberId=${subscriberId}&userId=${identityUserId}`;
        try {
            await API.get.verifyAccount(queryParams);
            mixpanel.track(EMixpanelEvent.actionCompleted, { key: 'consumer_create_account' });
        } catch (error) {
            console.error('Failed to verify passcode');
            throw error.response?.status;
        }
    };

    const resendVerifyPasscode = async () => {
        const postObject: TRequestVerifyPost = {
            email,
            userId: identityUserId,
            usePasscode: true,
            ...(subscriberId && { subscriberId }),
        };
        try {
            await API.post.requestVerify(gatewayAPIBaseUrl, postObject);
        } catch (error) {
            console.error('Failed to resend passcode');
            throw error.response?.status;
        }
    };

    useEffect(() => {
        getInitialData();
    }, []);

    useEffect(() => {
        if (countriesList.length && !country) {
            setCountry(countriesList[0].code);
        }
    }, [countriesList]);

    useEffect(() => {
        if (createAccountUiState === ECreateAccountUiState.success) {
            // local storage used to retrieve login link after verification
            putFullLoginLinkInLocalStorage();
        }
    }, [createAccountUiState]);

    return (
        <CreateAccountContext.Provider
            value={{
                createAccountUiState,
                setCreateAccountUiState,
                isPrePopulated,
                firstName,
                setFirstName,
                firstNameIsValid,
                firstNameValidationMessage,
                lastName,
                setLastName,
                lastNameIsValid,
                lastNameValidationMessage,
                email,
                emailIsValid,
                emailValidationMessage,
                setEmail,
                password,
                passwordIsValid,
                setPassword,
                country,
                setCountry,
                countryIsValid,
                addFamily,
                skipAddFamily,
                submitCreateAccountForm,
                errorMessage,
                tosAgreed,
                setTosAgreed,
                tosIsValid,
                marketingOptIn,
                setMarketingOptIn,
                submitVerifyPasscode,
                resendVerifyPasscode,
            }}
        >
            {children}
        </CreateAccountContext.Provider>
    );
};

export default CreateAccountContext;
